import { LoaderData } from "~/types";
import { FormattedLeaseData } from "~/types/lease";
import { EMPTY_FORMATTED_LEASE_INFO, getLeaseInfo } from "./getLeaseInfo";
import { getBackgroundCheckInfo } from "./getBackgroundCheckInfo";
import { getMoveExamInfo } from "./getMoveExamInfo";
import {
  EMPTY_UNIT,
  getBuildingDetails,
  getCommentList,
  getPropertyManagerByTenantID,
  getUnitDetails,
} from "~/services/server";
import { getOfferInfo } from "./getOfferInfo";
import * as APITypes from "~/API";
import { getApplicationDetails } from "./getApplicationDetails";

const EMPTY_PROPERTY_MANAGER: APITypes.User = {
  id: "",
  __typename: "User",
  createdAt: "",
  updatedAt: "",
  _version: 0,
  _lastChangedAt: 0,
};

const EMPTY_FORMATTED_LEASE: FormattedLeaseData = {
  leaseInfo: EMPTY_FORMATTED_LEASE_INFO,
  managerInfo: EMPTY_PROPERTY_MANAGER,
  entityType: APITypes.EntityType.Unit,
  entityDetails: EMPTY_UNIT,
  advanceLeaseAction: "_Review_Application",
  cancelLeaseAction: "_Deny_Application",
};

/**
 * Retrieves and formats comprehensive lease data including all related entities and workflow state.
 *
 * This function orchestrates the retrieval of a complete lease record by fetching:
 * - Basic lease information
 * - Entity details (Unit or Building)
 * - Property manager information
 * - Application details and status
 * - Background check information
 * - Offer details and status
 * - Move-in examination data
 * - Move-out examination data
 *
 * Based on the current status of these components, it determines the appropriate
 * next actions available for advancing or canceling the lease workflow.
 *
 * @param leaseID - The unique identifier of the lease to retrieve
 * @param options - Optional configuration object to control which components to fetch
 * @param options.fetchApplication - Whether to fetch application details (default: true)
 * @param options.fetchBackground - Whether to fetch background check details (default: true)
 * @param options.fetchOffer - Whether to fetch offer details (default: true)
 * @param options.fetchMoveIn - Whether to fetch move-in examination details (default: true)
 * @param options.fetchMoveOut - Whether to fetch move-out examination details (default: true)
 * @param options.fetchComment - Whether to fetch comment details (default: true)
 * @param options.fetchManager - Whether to fetch property manager details (default: true)
 * @param options.fetchEntityDetails - Whether to fetch entity (unit/building) details (default: true)
 * @returns Promise<LoaderData<FormattedLeaseData>> - Object containing:
 *   - success: Boolean indicating if the operation was successful
 *   - data: Complete formatted lease data with all related entities and workflow state
 *   - error: Error object if any step in the data retrieval process failed
 */
export const getFormattedLease = async (
  leaseID: string,
  options: {
    fetchApplication?: boolean;
    fetchBackground?: boolean;
    fetchOffer?: boolean;
    fetchMoveIn?: boolean;
    fetchMoveOut?: boolean;
    fetchComment?: boolean;
    fetchManager?: boolean;
    fetchEntityDetails?: boolean;
  } = {}
): Promise<LoaderData<FormattedLeaseData>> => {
  // Set default values for options
  const {
    fetchApplication = true,
    fetchBackground = true,
    fetchOffer = true,
    fetchMoveIn = true,
    fetchMoveOut = true,
    fetchComment = true,
    fetchManager = true,
    fetchEntityDetails = true,
  } = options;
  const leaseInfo = await getLeaseInfo(leaseID);
  if (leaseInfo.error) {
    return {
      success: false,
      data: EMPTY_FORMATTED_LEASE,
      error: new Error("get lease info failed", leaseInfo.error),
    };
  }

  let lease: FormattedLeaseData = {
    leaseInfo: leaseInfo.data,
    managerInfo: EMPTY_PROPERTY_MANAGER,
    entityType: leaseInfo.data.entityType as APITypes.EntityType,
    entityDetails: EMPTY_UNIT,
    advanceLeaseAction: "_Review_Application",
    cancelLeaseAction: "_Deny_Application",
  };

  if (fetchEntityDetails) {
    if (lease.entityType === APITypes.EntityType.Unit) {
      const unitDetails = await getUnitDetails(leaseInfo.data.entityID);
      lease.entityDetails = unitDetails.data;
    } else if (lease.entityType === APITypes.EntityType.Building) {
      const unitDetails = await getBuildingDetails(leaseInfo.data.entityID);
      lease.entityDetails = unitDetails.data;
    }
  }

  if (fetchManager) {
    const manager = await getPropertyManagerByTenantID(
      leaseInfo.data.tenantID ?? ""
    );
    lease.managerInfo = manager.data;
  }

  if (fetchApplication) {
    const application = await getApplicationDetails(
      leaseInfo.data.applicationID || ""
    );
    if (application.error) {
      return {
        success: false,
        data: EMPTY_FORMATTED_LEASE,
        error: new Error("get application info failed", application.error),
      };
    }

    lease.application = application.data;
    if (application.data.status === "UnderReview") {
      lease.advanceLeaseAction = "_Approve_Application";
    }
  }

  if (fetchBackground) {
    const background = await getBackgroundCheckInfo(
      leaseInfo.data.backgroundCheckID || ""
    );
    if (background.error) {
      console.log("background ID", leaseInfo.data.backgroundCheckID);
      console.log("get background info failed", background.error);
      return {
        success: false,
        data: EMPTY_FORMATTED_LEASE,
        error: new Error("get background info failed", background.error),
      };
    } else {
      lease.background = background.data;

      if (background.data.status === "InProgress") {
        lease.advanceLeaseAction = "_Approve_Background";
        lease.cancelLeaseAction = "_Deny_Background";
      }

      if (background.data.status === "Approved") {
        lease.advanceLeaseAction = "_Send_Offer";
        lease.cancelLeaseAction = "_Cancel_Offer";
      }
    }
  }

  if (fetchOffer) {
    const offer = await getOfferInfo(leaseInfo.data.offerID || "");
    if (offer.error) {
      return {
        success: false,
        data: EMPTY_FORMATTED_LEASE,
        error: new Error("get offer info failed", offer.error),
      };
    } else {
      lease.offer = offer.data;

      if (
        offer.data.status === APITypes.OfferStatusType.TenantComplete ||
        offer.data.status === "InProgress"
      ) {
        lease.advanceLeaseAction = "_Validate_Offer";
        lease.cancelLeaseAction = "_Cancel_Offer";
      }

      if (
        offer.data.status === "Completed" ||
        offer.data.status === APITypes.OfferStatusType.LandlordComplete
      ) {
        lease.advanceLeaseAction = "_Start_MoveIn";
        lease.cancelLeaseAction = "_Cancel_MoveIn";
      }
    }
  }

  if (fetchMoveIn) {
    const moveIn = await getMoveExamInfo(leaseInfo.data.moveInID || "");
    if (moveIn.error) {
      return {
        success: false,
        data: EMPTY_FORMATTED_LEASE,
        error: new Error("get move in info failed", moveIn.error),
      };
    } else {
      lease.moveIn = moveIn.data;
      if (moveIn.data.status === "InProgress") {
        lease.advanceLeaseAction = "_Complete_MoveIn";
        lease.cancelLeaseAction = "_Cancel_MoveIn";
      }
    }
  }

  if (fetchMoveOut) {
    const moveOut = await getMoveExamInfo(leaseInfo.data.moveOutID || "");
    if (moveOut.error) {
      return {
        success: false,
        data: EMPTY_FORMATTED_LEASE,
        error: new Error("get move out info failed", moveOut.error),
      };
    } else {
      lease.moveOut = moveOut.data;
      if (moveOut.data.status === "InProgress") {
        lease.advanceLeaseAction = "_Complete_MoveOut";
        lease.cancelLeaseAction = "_Cancel_MoveOut";
      }

      if (
        moveOut.data.status === "Completed" ||
        moveOut.data.status === "Failed"
      ) {
        lease.leaseInfo.status = APITypes.LeaseStatusType.Expired;
      }
    }
  }

  if (fetchComment) {
    const comment = await getCommentList(leaseInfo.data.id || "");
    if (comment.error) {
      return {
        success: false,
        data: EMPTY_FORMATTED_LEASE,
        error: new Error("get comment info failed", comment.error),
      };
    } else if (comment.data.length > 0) {
      lease.comment = comment.data[0];
    }
  }

  return {
    success: true,
    data: lease,
  };
};
